import { ApolloLink } from '@apollo/client';
import { print } from 'graphql';
import { Observable } from 'rxjs';

import { GraphQL } from '@libs/trip-planner-interfaces';

// Must match OPERATIONS in smart-search-proxy; the proxy answers 404 for
// anything else.
const PROXY_OPERATIONS = [
  'searchItinerariesHotelSelection',
  'searchItineraries',
  'searchItinerarySuggestionPricesCheapest',
  'searchItinerarySuggestionPricesMostPopular',
  'searchProductAvailability',
  'getTicketBookingTokens',
];

export function shouldUseGoogleHotelProxy(
  userAgent: string,
  operationName: string | undefined,
  forceRest = false
): boolean {
  return (
    (forceRest ||
      /\bGoogle-HotelAdsVerifier(?:\/[\d.]+)?(?=$|[\s;)])/i.test(userAgent)) &&
    operationName !== undefined &&
    PROXY_OPERATIONS.includes(operationName)
  );
}

export function restrictGoogleHotelRecipe(
  recipe: GraphQL.RecipeQL,
  day = recipe.selectedDate
): GraphQL.RecipeQL {
  if (!day) {
    throw new Error('Google Hotel proxy requires a selected search day.');
  }

  return {
    ...recipe,
    startDateOnly: day,
    endDateOnly: day,
    selectedDate: day,
    metadata: {
      ...recipe.metadata,
      salesChannel: GraphQL.InputSaleChannelQL.MetaPackage,
    },
  };
}

interface ProxyVariables {
  recipe?: GraphQL.RecipeQL;
  request?: GraphQL.CheapestHotelStayRequestQL;
}

export function getGoogleHotelProxyVariables(
  operationName: string | undefined,
  variables: ProxyVariables
): ProxyVariables {
  if (operationName === 'searchProductAvailability') {
    const request = variables.request;
    if (!request?.day) {
      throw new Error('Google Hotel proxy requires a selected search day.');
    }
    return {
      ...variables,
      request: {
        ...request,
        recipe: restrictGoogleHotelRecipe(request.recipe, request.day),
      },
    };
  }

  if (!variables.recipe) {
    // e.g. getTicketBookingTokens: no recipe, nothing to restrict.
    return variables;
  }
  return {
    ...variables,
    recipe: restrictGoogleHotelRecipe(variables.recipe),
  };
}

export function createGoogleHotelProxyLink(
  baseUrl: string | undefined
): ApolloLink {
  return new ApolloLink(
    (operation) =>
      new Observable<ApolloLink.Result>((observer) => {
        const controller = new AbortController();

        async function request(): Promise<void> {
          if (!baseUrl?.trim()) {
            throw new Error(
              'Google Hotel proxy requires VITE_SMART_SEARCH_PROXY_URL.'
            );
          }

          const response = await fetch(
            `${baseUrl.trim().replace(/\/$/, '')}/graphql`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              signal: controller.signal,
              body: JSON.stringify({
                id: crypto.randomUUID(),
                type: 'subscribe',
                payload: {
                  operationName: operation.operationName,
                  query: print(operation.query),
                  variables: getGoogleHotelProxyVariables(
                    operation.operationName,
                    operation.variables
                  ),
                },
              }),
            }
          );

          if (!response.ok) {
            throw new Error(
              `Google Hotel proxy HTTP error: ${response.status}`
            );
          }

          // The proxy returns every upstream payload in stream order; replay
          // them so consumers see the same sequence as over the WebSocket.
          const frames: ApolloLink.Result[] = await response.json();
          for (const frame of frames) {
            observer.next(frame);
          }
          observer.complete();
        }

        void request().catch((error: unknown) => observer.error(error));
        return () => controller.abort();
      })
  );
}
